/**
 * Notification Hook
 *
 * Provides programmatic toast notifications throughout the app.
 * Use NotificationProvider at app root and hook in components.
 *
 * @example
 * // In app.tsx
 * <NotificationProvider>
 *   <App />
 * </NotificationProvider>
 *
 * @example
 * // In any component
 * const { showSuccess, showError } = useNotification();
 *
 * try {
 *   await saveItem();
 *   showSuccess('Item Saved', 'Your changes have been saved');
 * } catch (error) {
 *   showError('Save Failed', error.message);
 * }
 */

import type { ReactNode } from 'react';
import { createContext, useCallback, useContext } from 'react';
import { prefixes, useTypedTranslation } from "@/locales";
import { toast } from 'sonner';
import { IconNotification } from '@/components/_untitled/application/notifications/notifications';

interface NotificationContextValue {
    showSuccess: (title: string, message?: ReactNode, onClose?: () => void) => void;
    showError: (title: string, message?: ReactNode, actionButton?: { label: string; onClick: () => void }) => void;
    showWarning: (title: string, message?: ReactNode, onClose?: () => void) => void;
    showInfo: (title: string, message?: ReactNode, onClose?: () => void) => void;
}

const NotificationContext = createContext<NotificationContextValue | null>(null);

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
    const [tGeneral] = useTypedTranslation(prefixes('general'));

    const showSuccess = useCallback((title: string, message?: ReactNode, onClose?: () => void) => {
        toast.custom((toastId: string | number) => (
            <IconNotification
                color="success"
                title={ title }
                description={ message || '' }
                dismissLabel={ tGeneral('dismiss') }
                closeButtonLabel={ tGeneral('dismissNotification') }
                onClose={ () => {
                    toast.dismiss(toastId);
                    onClose?.();
                } }
            />
        ));
    }, [tGeneral]);

    const showError = useCallback((title: string, message?: ReactNode, actionButton?: {
        label: string;
        onClick: () => void
    }) => {
        toast.custom((toastId: string | number) => (
            <IconNotification
                color="error"
                title={ title }
                description={ message || '' }
                dismissLabel={ tGeneral('dismiss') }
                closeButtonLabel={ tGeneral('dismissNotification') }
                confirmLabel={ actionButton?.label }
                onConfirm={ actionButton ? () => {
                    toast.dismiss(toastId);
                    try {
                        actionButton.onClick();
                    } catch (error) {
                        console.error('Error in notification action button:', error);
                    }
                } : undefined }
                onClose={ () => toast.dismiss(toastId) }
            />
        ));
    }, [tGeneral]);

    const showWarning = useCallback((title: string, message?: ReactNode, onClose?: () => void) => {
        toast.custom((toastId: string | number) => (
            <IconNotification
                color="warning"
                title={ title }
                description={ message || '' }
                dismissLabel={ tGeneral('dismiss') }
                closeButtonLabel={ tGeneral('dismissNotification') }
                onClose={ () => {
                    toast.dismiss(toastId);
                    onClose?.();
                } }
            />
        ));
    }, [tGeneral]);

    const showInfo = useCallback((title: string, message?: ReactNode, onClose?: () => void) => {
        toast.custom((toastId: string | number) => (
            <IconNotification
                color="default"
                title={ title }
                description={ message || '' }
                dismissLabel={ tGeneral('dismiss') }
                closeButtonLabel={ tGeneral('dismissNotification') }
                onClose={ () => {
                    toast.dismiss(toastId);
                    onClose?.();
                } }
            />
        ));
    }, [tGeneral]);

    return (
        <NotificationContext.Provider value={ { showSuccess, showError, showWarning, showInfo } }>
            { children }
        </NotificationContext.Provider>
    );
};

export const useNotification = (): NotificationContextValue => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotification must be used within NotificationProvider');
    }
    return context;
};